/* eslint-disable react/no-array-index-key */
/* eslint-disable operator-linebreak */
import { useState, useCallback, useEffect } from 'react'
import isEqual from 'lodash/isEqual';
import queryString from 'query-string'
import { useNavigate } from 'react-router-dom'
import cn from 'classnames'

import { addOrRemove, toArray } from '../../../lib/utils'
import {
  LAB_ORDER_NOTES,
  ORDER_BY,
  SERVICE_TYPES,
  PAPER_TYPES,
  QUERYSTRING_PARSE_FORMAT,
  LAB_ORDER_STATUS,
  FILTER_COLORS,
} from '../../../lib/const'

import TopButton from '../../common/TopButton'
import { ReactComponent as CreateIcon } from '../../../images/icons/create.svg'
import { ReactComponent as UnbundledLineItemsIcon } from '../../../images/icons/unbundledLineItems.svg'
import Filter from '../../common/Filter'
import SearchBar from './SearchBar'
import StatusDropdown from './StatusDropdown'
import OrderByDropdown from './OrderByDropdown'
import DateDropdown from './DateDropdown'
import NotesDropdown from './NotesDropdown'
import ServiceDropdown from './ServiceDropdown'
import PaperTypeDropdown from './PaperTypeDropdown'

const LabOrdersHeader = ({ isAdmin, location }) => {
  const navigate = useNavigate();
  const queryParams = queryString.parse(location.search, QUERYSTRING_PARSE_FORMAT)
  const availableStatusKeys = Object.keys(LAB_ORDER_STATUS)

  const [status, setStatus] = useState(() => location.search
    ? toArray(queryParams.status).filter((statusKey) => availableStatusKeys.includes(statusKey))
    : availableStatusKeys.filter((statusKey) => !isAdmin || statusKey !== 'open'))
  const [isolatedStatus, setIsolatedStatus] = useState(null)
  const [paperTypes, setPaperTypes] = useState(() => isAdmin ? toArray(queryParams.papers).filter((value) => Object.keys(PAPER_TYPES).includes(value)) : [])
  const [service, setService] = useState(() => isAdmin ? toArray(queryParams.services)
    .map((value) => ({ matting: 'mat', mounting: 'mount', framing: 'frame', artist_proofs: 'artist_proof', test_prints: 'test_print' }[value] || value))
    .filter((value) => Object.keys(SERVICE_TYPES).includes(value)) : [])
  const [orderBy, setOrderBy] = useState(queryParams.order_by || null)
  const [startDate, setStartDate] = useState(queryParams.start_date || null)
  const [endDate, setEndDate] = useState(queryParams.end_date || null)
  const [urgent, setUrgent] = useState(isAdmin ? queryParams[LAB_ORDER_NOTES.urgent] ?? null : null)
  const [adminNote, setAdminNote] = useState(isAdmin ? queryParams[LAB_ORDER_NOTES.adminNote] ?? null : null)
  const [orderNote, setOrderNote] = useState(isAdmin ? queryParams[LAB_ORDER_NOTES.orderNote] ?? null : null)
  const [term, setTerm] = useState(queryParams.query || null)

  // const filters=localStorage.getItem("filter")

  const handleOnSearchChange = useCallback((_term) => {
    setTerm(_term)
  }, [])

  const handleToggleStatus = (value) => {
    setIsolatedStatus(null)
    setStatus((prev) => addOrRemove(prev, value))
  }
  const handleSelectStatus = (value) => {
    setIsolatedStatus((currentStatus) => (currentStatus === value ? null : value))
  }
  const handleResetStatus = useCallback(() => {
    setIsolatedStatus(null)
    setStatus([])
  }, [])
  const handleTogglePaperTypes = (value) => setPaperTypes((prev) => addOrRemove(prev, value))

  const handleResetPaperTypes = useCallback(() => {
    setPaperTypes([])
  }, [])
  const handleToggleService = (value) => setService((prev) => addOrRemove(prev, value))
  const handleResetService = useCallback(() => {
    setService([])
  }, [])

  const handleToggleOrderBy = useCallback((e) => setOrderBy(e.target.value), [])

  const handleToggleNotes = (value) => {
    switch (value) {
      case LAB_ORDER_NOTES.urgent:
        setUrgent((prev) => (prev ? null : value))
        break
      case LAB_ORDER_NOTES.adminNote:
        setAdminNote((prev) => (prev ? null : value))
        break
      case LAB_ORDER_NOTES.orderNote:
        setOrderNote((prev) => (prev ? null : value))
        break
      default:
        break
    }
  }

  const handleResetNotes = useCallback(() => {
    setUrgent(null)
    setAdminNote(null)
    setOrderNote(null)
  }, [])

  const handleStartDateChange = (value) => setStartDate(value)
  const handleEndDateChange = (value) => setEndDate(value)
  const handleResetDate = useCallback(() => {
    setStartDate('')
    setEndDate('')
  }, [])

  useEffect(() => {

    const searchObj = {
      status: isolatedStatus ? [isolatedStatus] : status,
      order_by: orderBy,
      query: term,
      ...(isAdmin && {
        papers: paperTypes,
        services: service,
        ...(urgent && { [LAB_ORDER_NOTES.urgent]: !!urgent }),
        ...(adminNote && { [LAB_ORDER_NOTES.adminNote]: !!adminNote }),
        ...(orderNote && { [LAB_ORDER_NOTES.orderNote]: !!orderNote }),
      }),
      start_date: startDate,
      end_date: endDate,
    };

    const newSearch = queryString.stringify(searchObj, QUERYSTRING_PARSE_FORMAT);
    const currentSearchObj = queryString.parse(location.search, QUERYSTRING_PARSE_FORMAT);
    if (!isEqual(currentSearchObj, queryString.parse(newSearch, QUERYSTRING_PARSE_FORMAT))) {
      navigate({ pathname: location.pathname, search: `?${newSearch}` }, { replace: true });
    }

  }, [status, orderBy, urgent, adminNote, orderNote, paperTypes, service, startDate, endDate, isolatedStatus, term])

  const hasAdditionalFilters =
    startDate ||
    endDate ||
    orderBy ||
    urgent ||
    adminNote ||
    orderNote ||
    paperTypes.length > 0 ||
    service.length > 0

  return (
    <div className="div-block-47" style={{ height: 'initial' }}>
      <div className="top-menu-controls lab-orders-top-menu-controls">
        <div className="grid-container-controls lab-orders-toolbar-controls">
          <SearchBar onChange={handleOnSearchChange} term={term} />
          <div className="lab-orders-status-filters">
            {availableStatusKeys.map((el) => (
              <Filter
                key={el}
                label={LAB_ORDER_STATUS[el].label}
                onDelete={() => handleToggleStatus(el)}
                onClick={() => handleSelectStatus(el)}
                filterColor={isolatedStatus === el ? `status-order ${el}` : undefined}
                hideDelete
              />
            ))}
          </div>
          <div className={cn('w-layout-grid grid-filters', { single: !isAdmin })}>
            {/* <StatusDropdown
              activeFilters={status}
              onChange={handleToggleStatus}
              onReset={handleResetStatus}
            /> */}
            {isAdmin && (
              <>
                <div className="grid-vertical-line" />
                <PaperTypeDropdown
                  activeFilters={paperTypes}
                  onChange={handleTogglePaperTypes}
                  onReset={handleResetPaperTypes}
                />
                <div className="grid-vertical-line" />
                <ServiceDropdown
                  activeFilters={service}
                  onChange={handleToggleService}
                  onReset={handleResetService}
                />
              </>
            )}
          </div>
          {/* <DateDropdown
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            currentValues={{ endDate, startDate }}
            onReset={handleResetDate}
          />

          <OrderByDropdown onChange={handleToggleOrderBy} active={orderBy} /> */}
          {isAdmin && (
            <NotesDropdown
              isActiveUrgent={urgent}
              isActiveAdminNotes={adminNote}
              isActiveOrderNote={orderNote}
              onChange={handleToggleNotes}
              onReset={handleResetNotes}
            />
          )}
        </div>
        <div className="top-button on-grid">
          {/* {isAdmin && (
            <TopButton
              withTooltip
              label="Unbundled Line Items"
              to="/lab-orders/unbundled-line-items"
              icon={UnbundledLineItemsIcon}
            />
          )} */}
          <TopButton label="New Order" to="/print-order/create" icon={CreateIcon} />
        </div>
      </div>
      <div
        className={`w-layout-grid grid-filter-info ${hasAdditionalFilters ? 'margin-top' : ''}`}
      >
        {startDate && (
          <Filter
            label={`SINCE: ${startDate}`}
            onDelete={() => {
              setStartDate(null)
            }}
          />
        )}
        {endDate && (
          <Filter
            label={`UNTIL: ${endDate}`}
            onDelete={() => {
              setEndDate(null)
            }}
          />
        )}
        {orderBy && <Filter label={ORDER_BY[orderBy].label} onDelete={() => setOrderBy(null)} />}

        {urgent && <Filter label="Urgent" onDelete={() => setUrgent(null)} />}
        {adminNote && <Filter label="Admin Note" onDelete={() => setAdminNote(null)} />}
        {orderNote && <Filter label="Order Note" onDelete={() => setOrderNote(null)} />}

        {paperTypes.map((el, index) => (
          <Filter
            key={index}
            label={PAPER_TYPES[el].label}
            onDelete={() => handleTogglePaperTypes(el)}
            filterColor={FILTER_COLORS[PAPER_TYPES[el].label]}
          />
        ))}
        {service.map((el, index) => (
          <Filter
            key={index}
            label={SERVICE_TYPES[el].label}
            onDelete={() => handleToggleService(el)}
            filterColor={FILTER_COLORS[SERVICE_TYPES[el].label]}
          />
        ))}
      </div>
    </div>
  )
}

export default LabOrdersHeader
